// Zero-dependency country detection from the browser's own timezone.
//
// Why timezone (not IP): the browser reports its timezone synchronously via
// Intl, with no API call, no key, no database file, and nothing to refresh
// monthly — IANA zone→country assignments are effectively stable. It also
// reflects the user's *device* locale, which is usually a better "where am I
// from" signal than IP (a VPN changes the IP, not the device timezone).
//
// Unknown / unmapped zones fall back to the caller's default (India). This map
// covers the common zones for the populous countries; anything missing simply
// lands on the fallback, which is the same as the previous fixed default — so
// there is no regression, only an improvement for the zones we do map.

export const TIMEZONE_TO_COUNTRY = {
  // ── South Asia ────────────────────────────────────────────────────────────
  "Asia/Kolkata": "IN", "Asia/Calcutta": "IN",
  "Asia/Karachi": "PK", "Asia/Dhaka": "BD", "Asia/Colombo": "LK",
  "Asia/Kathmandu": "NP", "Asia/Thimphu": "BT", "Asia/Kabul": "AF",

  // ── Middle East ───────────────────────────────────────────────────────────
  "Asia/Tehran": "IR", "Asia/Baghdad": "IQ", "Asia/Riyadh": "SA",
  "Asia/Dubai": "AE", "Asia/Qatar": "QA", "Asia/Kuwait": "KW",
  "Asia/Bahrain": "BH", "Asia/Muscat": "OM", "Asia/Amman": "JO",
  "Asia/Beirut": "LB", "Asia/Damascus": "SY", "Asia/Jerusalem": "IL",
  "Asia/Gaza": "PS", "Asia/Hebron": "PS",

  // ── Caucasus & Central Asia ───────────────────────────────────────────────
  "Asia/Baku": "AZ", "Asia/Yerevan": "AM", "Asia/Tbilisi": "GE",
  "Asia/Almaty": "KZ", "Asia/Aqtobe": "KZ", "Asia/Tashkent": "UZ",
  "Asia/Ashgabat": "TM", "Asia/Bishkek": "KG", "Asia/Dushanbe": "TJ",

  // ── East & Southeast Asia ─────────────────────────────────────────────────
  "Asia/Shanghai": "CN", "Asia/Urumqi": "CN", "Asia/Hong_Kong": "HK",
  "Asia/Macau": "MO", "Asia/Taipei": "TW", "Asia/Tokyo": "JP",
  "Asia/Seoul": "KR", "Asia/Pyongyang": "KP", "Asia/Bangkok": "TH",
  "Asia/Ho_Chi_Minh": "VN", "Asia/Saigon": "VN", "Asia/Phnom_Penh": "KH",
  "Asia/Vientiane": "LA", "Asia/Yangon": "MM", "Asia/Rangoon": "MM",
  "Asia/Kuala_Lumpur": "MY", "Asia/Kuching": "MY", "Asia/Singapore": "SG",
  "Asia/Jakarta": "ID", "Asia/Makassar": "ID", "Asia/Jayapura": "ID",
  "Asia/Manila": "PH", "Asia/Brunei": "BN", "Asia/Dili": "TL",

  // ── Europe ────────────────────────────────────────────────────────────────
  "Europe/London": "GB", "Europe/Dublin": "IE", "Europe/Paris": "FR",
  "Europe/Berlin": "DE", "Europe/Madrid": "ES", "Atlantic/Canary": "ES",
  "Europe/Rome": "IT", "Europe/Amsterdam": "NL", "Europe/Brussels": "BE",
  "Europe/Lisbon": "PT", "Europe/Zurich": "CH", "Europe/Vienna": "AT",
  "Europe/Stockholm": "SE", "Europe/Oslo": "NO", "Europe/Copenhagen": "DK",
  "Europe/Helsinki": "FI", "Europe/Warsaw": "PL", "Europe/Prague": "CZ",
  "Europe/Budapest": "HU", "Europe/Bucharest": "RO", "Europe/Sofia": "BG",
  "Europe/Athens": "GR", "Europe/Moscow": "RU", "Europe/Kaliningrad": "RU",
  "Asia/Yekaterinburg": "RU", "Asia/Novosibirsk": "RU", "Asia/Vladivostok": "RU",
  "Europe/Kyiv": "UA", "Europe/Kiev": "UA", "Europe/Minsk": "BY",
  "Europe/Istanbul": "TR", "Europe/Zagreb": "HR", "Europe/Belgrade": "RS",
  "Europe/Bratislava": "SK", "Europe/Ljubljana": "SI", "Europe/Vilnius": "LT",
  "Europe/Riga": "LV", "Europe/Tallinn": "EE", "Europe/Luxembourg": "LU",
  "Europe/Reykjavik": "IS",

  // ── North & Central America ───────────────────────────────────────────────
  "America/New_York": "US", "America/Detroit": "US", "America/Chicago": "US",
  "America/Denver": "US", "America/Phoenix": "US", "America/Los_Angeles": "US",
  "America/Anchorage": "US", "Pacific/Honolulu": "US",
  "America/Toronto": "CA", "America/Vancouver": "CA", "America/Edmonton": "CA",
  "America/Winnipeg": "CA", "America/Halifax": "CA", "America/St_Johns": "CA",
  "America/Mexico_City": "MX", "America/Tijuana": "MX", "America/Monterrey": "MX",
  "America/Cancun": "MX", "America/Panama": "PA", "America/Costa_Rica": "CR",
  "America/Guatemala": "GT", "America/El_Salvador": "SV",
  "America/Tegucigalpa": "HN", "America/Managua": "NI", "America/Havana": "CU",
  "America/Santo_Domingo": "DO", "America/Puerto_Rico": "PR", "America/Jamaica": "JM",

  // ── South America ─────────────────────────────────────────────────────────
  "America/Sao_Paulo": "BR", "America/Bahia": "BR", "America/Fortaleza": "BR",
  "America/Manaus": "BR", "America/Argentina/Buenos_Aires": "AR",
  "America/Buenos_Aires": "AR", "America/Santiago": "CL", "America/Bogota": "CO",
  "America/Lima": "PE", "America/Caracas": "VE", "America/Guayaquil": "EC",
  "America/La_Paz": "BO", "America/Asuncion": "PY", "America/Montevideo": "UY",

  // ── Africa ────────────────────────────────────────────────────────────────
  "Africa/Cairo": "EG", "Africa/Lagos": "NG", "Africa/Johannesburg": "ZA",
  "Africa/Nairobi": "KE", "Africa/Accra": "GH", "Africa/Casablanca": "MA",
  "Africa/Algiers": "DZ", "Africa/Tunis": "TN", "Africa/Tripoli": "LY",
  "Africa/Khartoum": "SD", "Africa/Addis_Ababa": "ET", "Africa/Dar_es_Salaam": "TZ",
  "Africa/Kampala": "UG", "Africa/Dakar": "SN", "Africa/Abidjan": "CI",
  "Africa/Kinshasa": "CD", "Africa/Luanda": "AO", "Africa/Harare": "ZW",
  "Africa/Lusaka": "ZM", "Africa/Maputo": "MZ", "Africa/Windhoek": "NA",

  // ── Oceania ───────────────────────────────────────────────────────────────
  "Australia/Sydney": "AU", "Australia/Melbourne": "AU", "Australia/Brisbane": "AU",
  "Australia/Perth": "AU", "Australia/Adelaide": "AU", "Australia/Hobart": "AU",
  "Australia/Darwin": "AU", "Pacific/Auckland": "NZ", "Pacific/Fiji": "FJ",
  "Pacific/Port_Moresby": "PG",
};

// Returns an ISO country code from the device timezone, or null if unknown.
export function detectCountryFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_TO_COUNTRY[tz] || null;
  } catch {
    return null;
  }
}

// Detected country, or the fallback (default India) when the zone is unmapped.
export function detectDefaultCountry(fallback = "IN") {
  return detectCountryFromTimezone() || fallback;
}
